import { staticImplements } from 'utils/types'
import type {
	ServerSideCoupon,
	CouponsDependencies,
	PrbResponse,
	SearchBody,
	GetCouponQuery,
	PrbCoupon,
	Feature,
	DateTimeObject,
	SearchField,
} from './types'
import { createUrl, getECommerceDomainByEnv, sendRequest } from 'utils/utils'
import Infra from 'mobx/Infra'
import Application from 'mobx/Application'
import type { AxiosError, Method } from 'axios'
import Account from 'mobx/Account'
import CouponError, { ErrorCode } from './errors'
import type { Exception, Flags, Coupon, ExternalCoupon, LinkedCoupon, NonExternalCoupon } from 'shared-types/Coupon'
import { Channel, OrderType } from 'shared-types/Coupon'
import { EXTERNAL_ORDER_TYPES, INTERNAL_ORDER_TYPES } from 'types/Coupons'
import type { ShowSnackbarProps } from 'mobx/Infra/Infra.type'
import type AddressManagerV2Store from 'mobx/AddressManagerV2/store'

const getCouponFields = Object.freeze([
	Object.freeze({ name: 'relatedFeatures', operator: 'eq', value: 'displayInCouponWallet', logicalOperator: 'and' }),
] as const)

@staticImplements<CouponsDependencies>()
export default class CouponsRepository {
	static addressManagerV2Store: AddressManagerV2Store | null = null

	static setAddressManagerV2Store(addressManagerV2Store: AddressManagerV2Store): void {
		this.addressManagerV2Store = addressManagerV2Store
	}

	static isUserLoggedIn(): boolean {
		return !!Account.user
	}

	/**
	 * Sends a request to the Coupons API (Marketing Service) with the specified parameters
	 *
	 * @throws CouponError if the request fails. Attempts to extract the message and code from the response, but will default to error.message and ErrorCode.DEFAULT
	 *
	 */

	private static async fetch<T = void>(
		url: string,
		method: Method,
		body?: object,
		queryParams?: string | string[][] | Record<string, string> | URLSearchParams
	): Promise<T> {
		const baseUrl = createUrl(getECommerceDomainByEnv(), `/v1/tenants/${Infra.appParams.c}/`)

		let fullUrl = `${baseUrl}${url}`

		if (queryParams) {
			const params = new URLSearchParams(queryParams)
			fullUrl += `?${params.toString()}`
		}

		const authorizationHeader = Account.user ? await Account.getAuthorizationHeader(Application.backendChannel) : null

		return (await sendRequest(false, fullUrl, method, body, authorizationHeader).catch(
			(e: AxiosError<{ error?: { message?: string; code?: ErrorCode } }>) => {
				const { error } = e.response?.data ?? {}
				const message = error?.message ?? e.message
				throw new CouponError(message, error?.code ?? ErrorCode.DEFAULT)
			}
		)) as T
	}

	private static isValidCoupon(maybeCoupon: unknown): maybeCoupon is ServerSideCoupon {
		return maybeCoupon != null && typeof maybeCoupon === 'object' && 'id' in maybeCoupon && 'features' in maybeCoupon
	}

	private static formatServerSideCoupon(coupon: ServerSideCoupon): Coupon {
		const { features, code, totalUses, usesLeft, ...rest } = coupon
		let { availability, channels } = coupon

		// LOWERCASE ONLY
		const orderTypeMap: Record<Lowercase<string>, OrderType> = {
			peakup: OrderType.PICKUP,
			instore: OrderType.IN_STORE,
			drivethru: OrderType.DRIVETHROUGH,
			drivethrough: OrderType.DRIVETHROUGH,
			autoexpress: OrderType.DRIVETHROUGH,
		}

		const flagsMap: Record<Feature['name'], keyof Flags> = {
			requireLoginWallet: 'requireLogin',
			displayInCouponWallet: 'wallet',
		}

		const orderTypes: OrderType[] = rest.orderTypes.length
			? (rest.orderTypes
					.map((type) => (type.toLowerCase() in orderTypeMap ? orderTypeMap[type.toLowerCase() as Lowercase<typeof type>] : type))
					.filter((type) => (Object.values(OrderType) as string[]).includes(type)) as OrderType[])
			: Object.values(INTERNAL_ORDER_TYPES)

		const flags: Partial<Flags> = features
			.map((x) => ({ ...x, name: x.name in flagsMap ? flagsMap[x.name] : x.name }))
			.reduce((acc, { name, ...fields }) => ({ ...acc, [name]: { value: true, ...fields } }), {} as Partial<Flags>)

		const expiration = coupon.expiration == null ? undefined : new Date(coupon.expiration)
		const formattedExpiration = expiration && Intl.DateTimeFormat(Infra?.locale?.msg?.replace('_', '-') ?? 'en-US').format(expiration)
		const attachedAt = 'attachedAt' in coupon && typeof coupon.attachedAt === 'number' ? new Date(coupon.attachedAt) : undefined

		const dateTimeObjectToDate = ({ year, month, day, hour, minute }: DateTimeObject) => new Date(year, month - 1, day, hour, minute)

		// Default to all channels
		if (!channels || channels.length === 0) {
			channels = Object.values(Channel)
		}

		// Default to always available
		if (!availability || Object.keys(availability).length === 0 || availability.weekly.length === 0) {
			availability = {
				weekly: [
					{
						minuteOfWeek: 0,
						durationMins: 10080,
					},
				],
				exceptions: [],
			}
		}

		const exceptions =
			availability?.exceptions?.map(
				({ start, end, ...exception }): Exception => ({
					start: dateTimeObjectToDate(start),
					end: dateTimeObjectToDate(end),
					...exception,
				})
			) || []

		return {
			...rest,
			...(attachedAt && { attachedAt }),
			...(usesLeft != null && { usesLeft }),
			...(totalUses != null && { totalUses }),
			...(code && { code }),
			channels,
			availability: {
				...availability,
				exceptions,
			},
			orderTypes,
			flags,
			expiration,
			formattedExpiration,
		} as Coupon
	}

	static isLocalized(): boolean {
		if (!this.addressManagerV2Store) {
			throw new Error('AddressManagerV2 store is not set')
		}
		return this.addressManagerV2Store.alreadyLocalized || this.addressManagerV2Store.isUserLocalized()
	}

	private static async getCoupons(fields?: SearchField[]): Promise<Coupon[]> {
		const body: Partial<SearchBody> = { fields: [...getCouponFields, ...(fields ?? [])] }

		return (
			await this.fetch<ServerSideCoupon[]>('users/me/coupons/search', 'post', body).catch((error) => {
				if (error instanceof CouponError && error.code === ErrorCode.COUPON_NOT_ACTIVE_ERROR) {
					return []
				}

				throw error
			})
		)
			.map((coupon) => this.normaliseCoupon(coupon, false))
			.filter((coupon): coupon is Coupon => !!coupon)
			.map((coupon) => ({ ...coupon, flags: { ...coupon.flags, applied: { value: false } } }))
	}

	public static async getInternalCoupons(): Promise<NonExternalCoupon[]> {
		const internalField: SearchField = { name: 'type', operator: 'eq', value: 'internal', logicalOperator: 'or' }
		const showInMyCouponsField: SearchField = { name: 'relatedFeatures', operator: 'eq', value: 'showInMyCoupons', logicalOperator: 'or' }
		const linkedCoupons: SearchField = { name: 'type', operator: 'eq', value: 'linked', logicalOperator: 'or' }
    // This value is related to the EXTERNAL feature on internal tictuk discounts, not coupons with type `external`
    const externalCoupon: SearchField = { name: 'relatedFeatures', operator: 'neq', value: 'EXTERNAL', logicalOperator: 'and' }

		if (this.isUserLoggedIn()) {
			return this.getCoupons([internalField, showInMyCouponsField, linkedCoupons, externalCoupon]) as Promise<NonExternalCoupon[]>
		}

		return this.getCoupons([externalCoupon, internalField]) as Promise<NonExternalCoupon[]>
	}

	public static async getExternalCoupons(): Promise<ExternalCoupon[]> {
		const ignored = CouponsRepository.getIgnoreOrderTypes()

		if (!EXTERNAL_ORDER_TYPES.every((orderType) => ignored.includes(orderType))) {
			return this.getCoupons([{ name: 'type', operator: 'eq', value: 'external', logicalOperator: 'and' }]) as Promise<ExternalCoupon[]>
		}

		return []
	}

	private static normaliseCoupon(coupon: ServerSideCoupon, throwErr = true): Coupon | null {
		if (!this.isValidCoupon(coupon)) {
			if (throwErr) {
				throw new CouponError('Received invalid coupon from response', ErrorCode.DEFAULT)
			} else {
				return null
			}
		}

		const result = this.formatServerSideCoupon(coupon)
		const ignored = this.getIgnoreOrderTypes()

		const orderTypes = ignored.length ? result.orderTypes.filter((type) => !ignored.includes(type)) : result.orderTypes

		if (!orderTypes.length) {
			return null
		}

		return { ...result, orderTypes }
	}

	static async getCoupon(code: string): Promise<Coupon | null> {
		if (!this.addressManagerV2Store) {
			throw new Error('AddressManagerV2 store is not set')
		}

		let storeId: string | null | undefined = null

		if (this.isLocalized() && this.addressManagerV2Store.newLocalizationFlowEnabled) {
			storeId = this.addressManagerV2Store.actualFlow?.store?.id
		} else if (this.isLocalized() && localStorage.getItem('storeId')) {
			storeId = localStorage.getItem('storeId')
		}

		const queryParams: GetCouponQuery = {}

		if (storeId) {
			queryParams.storeId = storeId
		}

		return this.fetch<ServerSideCoupon>(`coupons/${code}`, 'get', {}, queryParams).then((coupon) => this.normaliseCoupon(coupon))
	}

	/**
	 * Adds a coupon to the current user's wallet
	 *
	 * @throws if the user already has this coupon, if the coupon doesn't exist
	 */
	static async linkCoupon(code: string): Promise<LinkedCoupon> {
		return this.fetch<ServerSideCoupon>('coupons', 'post', { code }).then((coupon) => this.normaliseCoupon(coupon)) as Promise<LinkedCoupon>
	}

	static async getPrbCode(id: string): Promise<PrbCoupon> {
		console.count(`Fetching PRB code for ${id}`)
		return this.fetch<PrbResponse>(`coupons/${id}/GENERATE_QR`, 'patch')
			.then(({ couponCode, couponUrl, couponData }: PrbResponse): PrbCoupon => {
				if (!couponCode || !couponUrl || typeof couponUrl !== 'string' || typeof couponUrl !== 'string') {
					throw new CouponError('Error at PRB response', ErrorCode.USERS_COUPON_GENERATE_PRB_CODE)
				}

				return { code: couponCode, url: couponUrl, data: couponData }
			})
			.catch(() => {
				throw new CouponError('Error at PRB response', ErrorCode.USERS_COUPON_GENERATE_PRB_CODE)
			})
	}

	static getIgnoreOrderTypes(): OrderType[] {
		return (Infra?.appParams?.features?.disableCouponsOfOrderType as OrderType[]) ?? []
	}

	static showSnackbar = (data: ShowSnackbarProps) => {
		Infra.showSnackbar(data)
	}
}
